import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useMergeRefs } from "../../../common/useFallbackRef";
import { FOCUS_FILTER, FOCUS_FIRST_OPTION, FOCUS_INPUT, FOCUS_LAST_OPTION, FOCUS_NEXT_OPTION, FOCUS_PREVIOUS_OPTION, FOCUS_SELECT_ALL_BUTTON, } from "./MultiSelectFilter.types";
import { MultiSelectFilterContext } from "./MultiSelectFilter.context";
import MultiSelectModel from "./model/MultiSelectModel";
import MultiSelectFilterOptionList from "./components/MultiSelectFilterOptionList";
import MultiSelectFilterSelectAllButton from "./components/MultiSelectFilterSelectAllButton";
import MutliSelectFilterFilter from "./components/MutliSelectFilterFilter";
import MultiSelectFilterInput from "./components/MultiSelectFilterInput";
import { useFocusEffect } from "./MultiSelectFilter.function";
var MultiSelectFilter = forwardRef(function (_a, ref) {
    var placeholder = _a.placeholder, id = _a.id, name = _a.name, options = _a.options, placeholderFilter = _a.placeholderFilter, filterFunction = _a.filterFunction, value = _a.value, onChange = _a.onChange, onBlur = _a.onBlur, ariaLabelledBy = _a.ariaLabelledBy, ariaDescribedBy = _a.ariaDescribedBy, hideFilter = _a.hideFilter, hideSelectAllButton = _a.hideSelectAllButton, disabled = _a.disabled;
    var _b = useState(""), filter = _b[0], setFilter = _b[1];
    var refInput = useRef(null);
    var refFilter = useRef(null);
    var refListBox = useRef(null);
    var refMultiSelectVisuel = useRef(null);
    var refSelectAllButton = useRef(null);
    var combinedRefMultiSelect = useMergeRefs([ref, refMultiSelectVisuel]);
    var matchesSearch = function (option, searchText) {
        if (filterFunction) {
            return filterFunction(option, searchText);
        }
        else if (option.name) {
            return option.name.toString().toLowerCase().includes(searchText.toLowerCase());
        }
        return false;
    };
    var multiSelectModel = useMemo(function () {
        return new MultiSelectModel(options, value, filter, matchesSearch);
    }, [options, value, filter, filterFunction]);
    var _c = useState(undefined), focusOn = _c[0], setFocusOn = _c[1];
    /**
     * ============================
     *  TOGGLE POP UP
     * ============================
     */
    var _d = useState(false), isPopupOpen = _d[0], setIsPopupOpen = _d[1];
    var openPopup = function (event) {
        if (disabled) {
            return;
        }
        setIsPopupOpen(true);
        event.stopPropagation();
        event.preventDefault();
    };
    var openPopupAndFocusFirstElement = function (event) {
        if (disabled) {
            return;
        }
        openPopup(event);
        if (!hideFilter) {
            setFocusOn(FOCUS_FILTER);
        }
        else if (!hideSelectAllButton) {
            setFocusOn(FOCUS_SELECT_ALL_BUTTON);
        }
        else {
            setFocusOn(FOCUS_FIRST_OPTION);
        }
    };
    var openPopupAndFocusFirstOption = function (event) {
        if (disabled) {
            return;
        }
        openPopup(event);
        setFocusOn(FOCUS_FIRST_OPTION);
    };
    var openPopupAndFocusLastOption = function (event) {
        if (disabled) {
            return;
        }
        openPopup(event);
        setFocusOn(FOCUS_LAST_OPTION);
    };
    var closePopup = function (event) {
        setIsPopupOpen(false);
        setFilter("");
        event.stopPropagation();
        event.preventDefault();
        setFocusOn(FOCUS_INPUT);
    };
    useFocusEffect(refInput, refFilter, refSelectAllButton, multiSelectModel, isPopupOpen, focusOn, setFocusOn, name);
    /**
     * ============================
     *  FOCUS NEW ELEMENT FUNCTIONS
     * ============================
     */
    var focusNextElementOfFilter = function () {
        if (!hideSelectAllButton) {
            setFocusOn(FOCUS_SELECT_ALL_BUTTON);
        }
        else {
            setFocusOn(FOCUS_FIRST_OPTION);
        }
    };
    var focusPreviousElementOfSelectAllButton = function () {
        if (!hideFilter) {
            setFocusOn(FOCUS_FILTER);
        }
        else {
            setFocusOn(FOCUS_FIRST_OPTION);
        }
    };
    var focusPreviousElementOfOptionList = function (event) {
        if (!hideSelectAllButton) {
            setFocusOn(FOCUS_SELECT_ALL_BUTTON);
        }
        else if (!hideFilter) {
            setFocusOn(FOCUS_FILTER);
        }
        else {
            closePopup(event);
        }
    };
    var focusNextElementOfOptionList = function (event) {
        if (!hideFilter) {
            setFocusOn(FOCUS_FILTER);
        }
        else if (!hideSelectAllButton) {
            setFocusOn(FOCUS_SELECT_ALL_BUTTON);
        }
        else {
            closePopup(event);
        }
    };
    /**
     * ============================
     *  KEYDOWN FUNCTION FOR LISTBOX
     * ============================
     */
    var onKeyDownListBox = function (event) {
        if (event.key === "Escape") {
            closePopup(event);
        }
    };
    /**
     * ============================
     *  CLICK OUTSIDE EVENT
     * ============================
     */
    var onClickOutside = function (event) {
        if (isPopupOpen &&
            refInput &&
            refInput.current &&
            !refInput.current.contains(event.target) &&
            refListBox &&
            refListBox.current &&
            !refListBox.current.contains(event.target)) {
            closePopup(event);
        }
    };
    useEffect(function () {
        document.addEventListener("mousedown", onClickOutside);
        return function unmount() {
            document.removeEventListener("mousedown", onClickOutside);
        };
    }, [onClickOutside]);
    var contextValue = useMemo(function () {
        return {
            id: id,
            name: name,
            multiSelectModel: multiSelectModel,
            onChange: onChange,
        };
    }, [id, name, multiSelectModel, onChange]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: combinedRefMultiSelect, role: "combobox", id: id, "aria-haspopup": "listbox", "aria-expanded": isPopupOpen ? "true" : "false", "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy },
            React.createElement(MultiSelectFilterContext.Provider, { value: contextValue },
                React.createElement(MultiSelectFilterInput, { ref: refInput, placeholder: placeholder, openPopupAndFocusFirstElement: openPopupAndFocusFirstElement, openPopupAndFocusFirstOption: openPopupAndFocusFirstOption, openPopupAndFocusLastOption: openPopupAndFocusLastOption, onBlur: onBlur, isPopupOpen: isPopupOpen, closePopup: closePopup, disabled: disabled }),
                React.createElement("div", { ref: refListBox, className: "ssg-multi-select-filter__listgroup" },
                    React.createElement("div", { role: "listbox", "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, className: "ssg-multi-select-filter__listbox", style: { display: isPopupOpen ? "inherit" : "none" }, onKeyDown: onKeyDownListBox },
                        hideFilter ? undefined : (React.createElement(MutliSelectFilterFilter, { ref: refFilter, setFilter: setFilter, placeholder: placeholderFilter, focusNextElement: focusNextElementOfFilter, focusPreviousElement: function () { return setFocusOn(FOCUS_FIRST_OPTION); } })),
                        hideSelectAllButton ? undefined : (React.createElement(MultiSelectFilterSelectAllButton, { ref: refSelectAllButton, focusPreviousElement: focusPreviousElementOfSelectAllButton, focusNextElement: function () { return setFocusOn(FOCUS_FIRST_OPTION); } })),
                        React.createElement(MultiSelectFilterOptionList, { focusPreviousElement: focusPreviousElementOfOptionList, focusNextElement: focusNextElementOfOptionList, focusFirstOption: function () { return setFocusOn(FOCUS_FIRST_OPTION); }, focusLastOption: function () { return setFocusOn(FOCUS_LAST_OPTION); }, focusNextOption: function () { return setFocusOn(FOCUS_NEXT_OPTION); }, focusPreviousOption: function () { return setFocusOn(FOCUS_PREVIOUS_OPTION); } })))))));
});
export default MultiSelectFilter;
